import type { TFunction } from "i18next";
import { formatMoney } from "../../billing/utils/money";
import { LateCancellationOutcome } from "../../organization/enums/LateCancellationOutcome";
import { CancellationFee } from "../../organization/models/CancellationPolicy";
import { CancellationPreviewResponse } from "../contracts/CancellationPreviewResponse";
import { CancellationCharge } from "../enums/CancellationCharge";

/** The parts of a policy the sentence needs (a CancellationPolicy fits). */
export interface PolicySentenceInput {
  cutoffHours: number;
  lateOutcome: LateCancellationOutcome;
  fee: Pick<CancellationFee, "name" | "amountMinorUnits" | "currency"> | null;
}

/** "Ausfallgebühr (15,00 €)" — the amount in the viewer's locale. */
const feeText = (
  fee: Pick<CancellationFee, "name" | "amountMinorUnits" | "currency">,
  t: TFunction,
  locale: string,
): string =>
  t("cancellationPolicy.consequence.fee", {
    name: fee.name,
    amount: formatMoney(fee.amountMinorUnits, fee.currency, locale),
  });

/** What a late cancellation costs: "the full price", "Fee (€15.00)" or "no charge". */
export const policyConsequence = (policy: PolicySentenceInput, t: TFunction, locale: string): string => {
  if (policy.lateOutcome === LateCancellationOutcome.ChargedFull) {
    return t("cancellationPolicy.consequence.full");
  }
  if (policy.lateOutcome === LateCancellationOutcome.ChargedFee && policy.fee) {
    return feeText(policy.fee, t, locale);
  }
  return t("cancellationPolicy.consequence.waived");
};

/**
 * The one-line policy shown before booking. A zero cutoff means every cancellation
 * before the start is free, so the late outcome can never apply.
 */
export const policySentence = (policy: PolicySentenceInput, t: TFunction, locale: string): string => {
  if (policy.cutoffHours === 0) {
    return t("cancellationPolicy.sentence.free");
  }
  return t("cancellationPolicy.sentence.cutoff", {
    count: policy.cutoffHours,
    consequence: policyConsequence(policy, t, locale),
  });
};

/** The cancel dialog's question: free, or late with what will be charged. */
export const cancellationConfirmText = (
  preview: CancellationPreviewResponse,
  t: TFunction,
  locale: string,
): string => {
  if (preview.charge === CancellationCharge.None) {
    return t("portal.cancel.confirmFree");
  }
  const consequence =
    preview.charge === CancellationCharge.Fee && preview.feeName
      ? feeText(
          {
            name: preview.feeName,
            amountMinorUnits: preview.feeAmountMinorUnits ?? 0,
            currency: preview.currency ?? "",
          },
          t,
          locale,
        )
      : t("cancellationPolicy.consequence.full");
  return t("portal.cancel.confirmLate", { count: preview.cutoffHours, consequence });
};
